// components/portal/BrandNewsTab.tsx
//
// The brand's News tab (lib/brands/brandNews.ts): the summary a leasing manager reads first, in a
// bubble, then the articles as a classic list, each with its own image, source, date and main idea.
// Read through the kernel (loadBrandNews) and cached on the brand's runtime overlay, like the
// Expansion tab, so re-opening the tab does not re-query.
import React, { useEffect, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import type { BrandNews, BrandNewsState } from '#types/types';

const formatDate = (iso: string | null, lang: string) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** The article's own image; nothing at all when it fails to load, rather than a broken frame. */
const Thumb = ({ src }: { src: string | null }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <div className="h-[84px] w-[128px] shrink-0 overflow-hidden rounded-[12px] bg-[#1B1B1B]">
      <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)} className="h-full w-full object-cover" />
    </div>
  );
};

const Article = ({ article, lang }: { article: BrandNews['articles'][number]; lang: string }) => (
  <a href={article.url} target="_blank" rel="noreferrer" className="group -mx-3 flex items-start gap-4 rounded-[16px] px-3 py-3 transition-colors hover:bg-white/[0.04]">
    <div className="min-w-0 flex-1">
      <div className="text-[#8E8E8E]" style={{ fontSize: 12 }}>{[article.source, formatDate(article.publishedAt, lang)].filter(Boolean).join(' · ')}</div>
      <div className="mt-0.5 line-clamp-2 font-medium text-[#ECECEC] group-hover:text-white" style={{ fontSize: 15, lineHeight: '21px' }}>{article.title}</div>
      <div className="mt-1 line-clamp-2 text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '19px' }}>{lang === 'fr' ? article.idea.fr : article.idea.en}</div>
    </div>
    <Thumb src={article.image} />
  </a>
);

export const BrandNewsTab = ({ brandId, lang }: { brandId: string; lang: string }) => {
  const { kernel } = useKernel();
  const state = useKernelStore((s) => (s.entityKernelState[`Brand:${brandId}`]?.state as { runtime?: { news?: BrandNewsState } } | undefined)?.runtime?.news);
  useEffect(() => {
    // The kernel skips the call when this brand's news is already loading or loaded.
    if (brandId) void kernel.current?.loadBrandNews(brandId);
  }, [brandId, kernel]);
  const fr = lang === 'fr';

  if (!state || state.status === 'loading') {
    // The tab's own shape in a shimmer: the summary bubble, then article rows with their picture.
    return (
      <div className="skeleton-in py-2" aria-busy="true" role="status" aria-label={fr ? 'Lecture des actualités' : 'Reading the news'}>
        <div className="space-y-2.5 rounded-[20px] bg-[#1B1B1B] px-5 py-4">
          {['30%', '94%', '88%', '52%'].map((w) => <div key={w} className="skeleton h-3 rounded-full" style={{ width: w }} />)}
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-4 py-3">
            <div className="min-w-0 flex-1 space-y-2.5 pt-1">
              <div className="skeleton h-2.5 w-28 rounded-full opacity-60" />
              <div className="skeleton h-3.5 rounded-full" style={{ width: ['82%', '70%', '76%', '64%'][i] }} />
              <div className="skeleton h-3 w-[90%] rounded-full opacity-70" />
            </div>
            <div className="skeleton h-[84px] w-[128px] shrink-0 rounded-[12px]" />
          </div>
        ))}
      </div>
    );
  }
  if (state.status === 'failed') {
    return <p className="py-6 text-[#8E8E8E]" style={{ fontSize: 14 }}>{fr ? 'Actualités indisponibles pour le moment.' : 'News unavailable right now.'}</p>;
  }
  const news = state.news;
  if (!news?.articles.length) {
    return <p className="py-6 text-[#8E8E8E]" style={{ fontSize: 14 }}>{fr ? 'Aucune actualité récente sur cette enseigne.' : 'No recent news about this brand.'}</p>;
  }
  const summary = fr ? news.summary.fr : news.summary.en;
  return (
    <div className="py-2">
      {summary ? (
        <section className="rounded-[20px] bg-[#1B1B1B] px-5 py-4" aria-label={fr ? "Synthèse de l'actualité" : 'News summary'}>
          <div className="text-[#8E8E8E]" style={{ fontSize: 12 }}>
            {fr ? 'Synthèse' : 'Summary'} · {fr ? `${news.articles.length} articles, mis à jour le ${formatDate(news.at, lang)}` : `${news.articles.length} articles, updated ${formatDate(news.at, lang)}`}
          </div>
          <p className="mt-1.5 text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '22px' }}>{summary}</p>
        </section>
      ) : null}
      <div className="mt-3 flex flex-col">
        {news.articles.map((article) => <Article key={article.url} article={article} lang={lang} />)}
      </div>
    </div>
  );
};
